'use client'

import React, { useState, useRef } from 'react'
import { cn } from '@/utils/extra'
import { ChevronRight } from 'lucide-react'
import EmailField from './emailField'
import SubmitButton from './button'
import P from './p'
import H2 from './h2'
import InputTextField from './inputField'

export default function OverlayForm ({ onSkip, ...props }) {
  const [value, setValue] = useState('')
  const [nameValue, setNameValue] = useState('')
  const [nameError, setNameError] = useState(null)
  const [emailError, setEmailError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [closing, setClosing] = useState(false)
  const inputRef = useRef(null)

  // Layout alignment logic
  let justify = 'justify-center'
  if (props?.style?.layout === 'left') justify = 'justify-start'
  if (props?.style?.layout === 'right') justify = 'justify-end'

  // Helper to close overlay
  const closeOverlay = () => {
    setClosing(true)
    setTimeout(() => {
      const overlay = document.querySelector('.player-form-overlay')
      if (overlay) overlay.classList.add('hidden')
      setClosing(false)
      setLoading(false)
      setNameError(null)
      setEmailError(null)
      window.widgetSkippedOrSubmitted = true
      if (props.visibility !== 'after-playback') {
        window.videoTag.play()
      }
    }, 200) // match animation duration
  }

  // Form submit handler
  const _handleSubmit = async (e) => {
    e.preventDefault()
    setNameError(null)
    setEmailError(null)
    setLoading(true)

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    // if nameValue and email are both null then error out
    if (!nameValue && !isValidEmail) {
      // set error based on the email field label
      // setError(props.elements.find((widget) => widget.type === 'text_field')?.label + ' and ' + props.elements.find((widget) => widget.type === 'email_field')?.label + ' are required')
      setNameError('Name is required')
      setEmailError('Email is required')
      setLoading(false)
      return
    }
    // if email is invalid then error out
    if (!isValidEmail) {
      // setError(props.elements.find((widget) => widget.type === 'email_field')?.label + ' is invalid')
      setEmailError('Email is invalid')
      setLoading(false)
      return
    }

    // if nameValue is null then error out
    if (!nameValue) {
      // setError(props.elements.find((widget) => widget.type === 'text_field')?.label + ' is required')
      setNameError('Name is required')
      setLoading(false)
      return
    }

    const response = await fetch('/api/widget', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        data: { email: value, name: nameValue },
        asset_id: props.assetId,
        collection_id: props.collectionId,
        organization_id: props.organizationId,
        widget_id: props.widgetId,
        playback_time_instant_milli: window.videoTag?.currentTime
      })
    })

    fetch('/api/webhook', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-gumlet-agent': 'gumlet-embed'
      },
      body: JSON.stringify({
        event: 'event.embed.form_submitted',
        payload: {
          collectionId: props.collectionId,
          playbackTimeInstantMilli: window.videoTag?.currentTime,
          email: value,
          name: nameValue
        },
        assetId: props.assetId
      })
    })

    const data = await response.json()
    if (data.ok) {
      try {
        const testKey = '__storage_test__'
        window.localStorage.setItem(testKey, 'test')
        window.localStorage.removeItem(testKey)
        window.localStorage.setItem(props.cookieId, 'true')
      } catch (_) {}
    }

    // Animate closing after submission
    closeOverlay()
  }

  return (
    <div
      className={cn(
        'player-form-overlay form-embed-player-innerwrapper',
        'fixed inset-0 z-[3000] flex items-center p-4',
        justify
      )}
      style={{ display: 'none' }}
    >
      {/* Backdrop */}
      <div className='absolute inset-0 bg-black/90 backdrop-blur-sm' />

      {/* Skip Button */}
      {props.skippable && (
        <button
          type='button'
          onClick={() => {
            closeOverlay()
            onSkip?.()
          }}
          className={cn(
            'absolute top-6 right-6 rounded-full border border-zinc-300 bg-white/90 background-blur-sm px-5 py-1 text-xs sm:text-sm lg:text-sm font-medium text-zinc-700 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500'
          )}
        >
          Skip to Video
          <ChevronRight className='inline-block h-4 w-4' />
        </button>
      )}

      {/* Card */}
      <div
        className={cn(
          'relative isolate flex flex-col gap-8 p-6 sm:p-8 lg:p-12',
          'w-full max-w-md sm:max-w-xl lg:max-w-2xl',
          'after:pointer-events-none after:absolute after:inset-0 after:inset-ring after:inset-ring-white/15 after:rounded-2xl',
          'transform transition-all duration-400',
          closing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        )}
      >
        <form
          className={cn('space-y-4')}
          onSubmit={(e) => {
            e.preventDefault()
            _handleSubmit(e)
          }}
        >
          {props.elements.map((widget, index) => {
            if (widget.type === 'title') {
              return (
                <H2
                  key={`${widget.type}-${index}`}
                  text={widget.label}
                  className={props?.style?.layout}
                />
              )
            } else if (widget.type === 'sub_title') {
              return (
                <P
                  key={`${widget.type}-${index}`}
                  text={widget.label}
                  className={props?.style?.layout}
                />
              )
            } else if (widget.type === 'email_field') {
              return (
                <EmailField
                  key={`${widget.type}-${index}`}
                  label={widget.label}
                  placeholder={widget.placeholder}
                  value={value}
                  setValue={setValue}
                  ref={inputRef}
                  error={emailError}
                />
              )
            } else if (widget.type === 'text_field') {
              return (
                <InputTextField
                  key={`${widget.type}-${index}`}
                  label={widget.label}
                  placeholder={widget.placeholder}
                  value={nameValue}
                  setValue={setNameValue}
                  ref={inputRef}
                  error={nameError}
                />
              )
            } else if (widget.type === 'button') {
              return (
                <SubmitButton
                  key={`${widget.type}-${index}`}
                  label={widget.label}
                  loading={loading}
                />
              )
            }
            return null
          })}
        </form>
      </div>
    </div>
  )
}
